import { useQuery } from "@tanstack/react-query";
import {
  onboardingApi,
  onboardingKeys,
  type OnboardingProgressResponse,
  type OnboardingStep,
} from "../features/onboarding/onboardingApi";
import { useVenue } from "./VenueContext";

export interface VenueReadiness {
  progress: OnboardingProgressResponse | undefined;
  isLoading: boolean;
  /** Backend lifecycle string — `NOT_LIVE` | `READY` | `LIVE` (progress wins over the bootstrap value). */
  lifecycle: string | undefined;
  /** Publicly bookable — the public booking widget only works once this is true. */
  isLive: boolean;
  /** Mandatory onboarding done (READY or LIVE) — eligible to request activation. */
  isReady: boolean;
  mandatoryComplete: boolean;
  missingMandatory: OnboardingStep[];
}

/**
 * The shared venue-readiness signal — the onboarding progress the wizard already fetches, lifted into a
 * hook the whole app can read (setup banner, overview card, per-feature "not live yet" gates). Same query
 * key as the wizard (`onboardingKeys.progress`), so it's one cached request. Falls back to the bootstrap
 * `venue.lifecycle` before the progress query resolves so gating never flickers "live" on load.
 */
export function useOnboardingProgress(): VenueReadiness {
  const { venueId, venue } = useVenue();
  const query = useQuery({
    queryKey: onboardingKeys.progress(venueId),
    queryFn: () => onboardingApi.getProgress(venueId),
    staleTime: 60_000,
    enabled: venueId.length > 0,
  });

  const progress = query.data;
  const lifecycle = progress?.lifecycle ?? venue.lifecycle;
  const isLive = lifecycle?.toUpperCase() === "LIVE";
  const isReady = isLive || lifecycle?.toUpperCase() === "READY";

  return {
    progress,
    isLoading: query.isLoading,
    lifecycle,
    isLive,
    isReady: isReady ?? false,
    mandatoryComplete: progress?.mandatoryComplete ?? isReady ?? false,
    missingMandatory: progress?.missingMandatory ?? [],
  };
}
